import { useEffect, useState } from "react";
import { api, is4xxApiError } from "@/lib/api-client";

// ─────────────────────────────────────────────────────────────────────────────
// CalibrationStatus
//
// Phase 1.5 WU-2 (R-126 backend) — re-keyed from a single category fetch to a
// per-(profileId, fieldId) fan-out. The legacy OCR catalog (133 semantic
// FieldTypes keyed by `documentCategory`) was replaced in Phase 1 by Capture's
// per-(profileId, fieldId) model; this panel now speaks that model directly.
//
// The component fans out one fetch per field — at typical form sizes (~35
// fields) this is well under a second total and avoids needing a batch
// endpoint. If perf becomes a concern, a `POST /api/ocr/calibration/status/batch`
// can be added later.
// ─────────────────────────────────────────────────────────────────────────────

interface FieldStatus {
  profileId: string;
  fieldId: string;
  sampleCount: number;
  currentFloor: number | null;
  recommendedFloor: number | null;
  lastUpdatedAt: string | null;
}

interface FieldRef {
  /** FieldDefinition id — the stable OCR fieldId. */
  id: string;
  /** Operator-facing label. */
  name: string;
}

interface CalibrationStatusProps {
  tenantId: string;
  /** JobTypeForm id — the stable OCR profileId. */
  profileId: string;
  /** Fields whose calibration status to display. Empty → whole-panel empty state. */
  fields: FieldRef[];
}

type RowState =
  | { kind: "loading" }
  | { kind: "ok"; status: FieldStatus }
  | { kind: "empty" }
  | { kind: "error"; message: string };

export function CalibrationStatus({
  tenantId,
  profileId,
  fields,
}: CalibrationStatusProps) {
  const [rows, setRows] = useState<Record<string, RowState>>({});

  useEffect(() => {
    if (!tenantId || !profileId || fields.length === 0) return;

    let cancelled = false;
    // Seed every row as loading so the UI renders predictably even if some
    // fetches take longer than others.
    setRows(
      Object.fromEntries(fields.map((f) => [f.id, { kind: "loading" } as RowState])),
    );

    for (const field of fields) {
      (async () => {
        try {
          const data = await api.get<FieldStatus>(
            `/ocr/calibration/status?organizationId=${tenantId}&profileId=${profileId}&fieldId=${field.id}`,
          );
          if (cancelled) return;
          setRows((prev) => ({
            ...prev,
            [field.id]:
              data.sampleCount === 0
                ? { kind: "empty" }
                : { kind: "ok", status: data },
          }));
        } catch (err) {
          if (cancelled) return;
          // 4xx → treat as no-data empty state (same convention as the legacy
          // panel; some envs have the OCR service off, others have no curve
          // yet — neither is a scary error worth alarming the operator).
          if (is4xxApiError(err)) {
            setRows((prev) => ({ ...prev, [field.id]: { kind: "empty" } }));
          } else {
            setRows((prev) => ({
              ...prev,
              [field.id]: {
                kind: "error",
                message: err instanceof Error ? err.message : "Unknown error",
              },
            }));
          }
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [tenantId, profileId, fields]);

  if (fields.length === 0) {
    return (
      <div className="calibration-status none">
        <p>No calibration data yet.</p>
        <p className="hint">
          Add fields to this profile and enable audit sampling to begin
          collecting samples.
        </p>
      </div>
    );
  }

  // E-058 — the loaded-but-all-zeros table used to read as `0 — — —` with no
  // explanation; the one fact that tells the admin what to do first is that
  // zeros mean sampling has never collected anything.
  const rowStates = fields.map((f) => rows[f.id] ?? { kind: "loading" as const });
  const allEmpty =
    rowStates.length > 0 && rowStates.every((r) => r.kind === "empty");

  return (
    <div className="calibration-status">
      {allEmpty && (
        <p className="rb-hint" data-testid="calibration-zero-samples-note">
          No samples collected yet — the zeros below mean audit sampling has
          never run for these fields. Enable audit sampling in the Audit
          Sampling section below to begin collecting ground truth; recommended
          floors appear here as samples accumulate.
        </p>
      )}
      <table className="rb-table cal-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Samples</th>
            <th>Current Floor</th>
            <th>Recommended Floor</th>
            <th>Last Updated</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => {
            const row = rows[field.id] ?? { kind: "loading" };
            return (
              <tr key={field.id}>
                <td>
                  <span className="cal-field-label">{field.name}</span>
                </td>
                {renderRowCells(row)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function renderRowCells(row: RowState) {
  switch (row.kind) {
    case "loading":
      return (
        <>
          <td colSpan={4} className="cal-value">
            Loading…
          </td>
        </>
      );
    case "empty":
      return (
        <>
          <td className="cal-value">0</td>
          <td className="cal-value">—</td>
          <td className="cal-value">—</td>
          <td className="cal-value">—</td>
        </>
      );
    case "error":
      return (
        <>
          <td colSpan={4} className="cal-value cal-value--error">
            Calibration status couldn't be loaded — try refreshing or check the
            OCR service connection.
          </td>
        </>
      );
    case "ok":
      return (
        <>
          <td className="cal-value">{row.status.sampleCount.toLocaleString()}</td>
          <td className="cal-value">
            {row.status.currentFloor != null ? row.status.currentFloor.toFixed(2) : "—"}
          </td>
          <td className="cal-value">
            {row.status.recommendedFloor != null
              ? row.status.recommendedFloor.toFixed(2)
              : "—"}
          </td>
          <td className="cal-value">
            {row.status.lastUpdatedAt
              ? new Date(row.status.lastUpdatedAt).toLocaleDateString()
              : "—"}
          </td>
        </>
      );
  }
}
